import React, { useEffect, useState } from 'react';
import { Button, Form, Input, Modal, Typography, message, notification } from 'antd';
import styled from 'styled-components/macro';
import { useCreateOwnershipTypeMutation, useUpdateOwnershipTypeMutation } from '../../../graphql/ownership.generated';
import { OwnershipTypeEntity } from '../../../types.generated';
import { OwnershipTypeBuilderState } from './table/types';
import { useTranslation } from '../../../i18n/useTranslation';

const NAME_INPUT_TEST_ID = 'ownership-type-name-input';
const DESCRIPTION_INPUT_TEST_ID = 'ownership-type-description-input';

const FormItemContainer = styled.div`
    display: flex;
    flex-direction: column;
`;

const FormItemTitle = styled(Typography.Text)`
    font-size: 14px;
    margin-bottom: 8px;
`;

const StyledFormItem = styled(Form.Item)`
    margin-bottom: 8px;
`;

type Props = {
    isOpen: boolean;
    onClose: () => void;
    refetch: () => void;
    ownershipType?: OwnershipTypeEntity;
};

export const OwnershipBuilderModal = ({ isOpen, onClose, refetch, ownershipType }: Props) => {
    const { t } = useTranslation();

    // State
    const [ownershipTypeBuilderState, setOwnershipTypeBuilderState] = useState<OwnershipTypeBuilderState>({
        name: ownershipType?.info?.name || ownershipType?.urn || '',
        description: ownershipType?.info?.description || '',
    });
    const [isLoading, setIsLoading] = useState(false);

    const setName = (name: string) => {
        setOwnershipTypeBuilderState({ ...ownershipTypeBuilderState, name });
    };
    const setDescription = (description: string) => {
        setOwnershipTypeBuilderState({ ...ownershipTypeBuilderState, description });
    };
    const [form] = Form.useForm();
    form.setFieldsValue(ownershipTypeBuilderState);

    // Side effects
    useEffect(() => {
        if (ownershipType) {
            const ownershipTypeName = ownershipType?.info?.name || ownershipType?.urn;
            const ownershipTypeDescription = ownershipType?.info?.description || '';
            setOwnershipTypeBuilderState({
                name: ownershipTypeName,
                description: ownershipTypeDescription,
            });
        } else {
            setOwnershipTypeBuilderState({
                name: '',
                description: '',
            });
        }
    }, [ownershipType]);

    // Queries
    const [createOwnershipTypeMutation] = useCreateOwnershipTypeMutation();
    const [updateOwnershipTypeMutation] = useUpdateOwnershipTypeMutation();

    const onCreateOwnershipType = () => {
        setIsLoading(true);

        if (ownershipTypeBuilderState.name) {
            createOwnershipTypeMutation({
                variables: {
                    input: {
                        name: ownershipTypeBuilderState.name,
                        description: ownershipTypeBuilderState.description,
                    },
                },
            })
                .then(() => {
                    setOwnershipTypeBuilderState({
                        name: '',
                        description: '',
                    });
                    setIsLoading(false);
                    onClose();
                    notification.success({
                        message: t('Success'),
                        description: t('Successfully created ownership type.'),
                        placement: 'topRight',
                        duration: 3,
                    });
                    setTimeout(() => {
                        refetch();
                    }, 3000);
                })
                .catch((e: unknown) => {
                    message.destroy();
                    setIsLoading(false);
                    if (e instanceof Error) {
                        message.error({
                            content: t('Failed to create ownership type'),
                            duration: 3,
                        });
                    }
                });
        }
    };

    const onUpdateOwnershipType = () => {
        if (ownershipType) {
            updateOwnershipTypeMutation({
                variables: {
                    urn: ownershipType?.urn || '',
                    input: {
                        name: ownershipTypeBuilderState.name,
                        description: ownershipTypeBuilderState.description,
                    },
                },
            })
                .then(() => {
                    setName('');
                    setDescription('');
                    onClose();
                    notification.success({
                        message: t('Success'),
                        description: t('Successfully updated ownership type.'),
                        placement: 'topRight',
                        duration: 3,
                    });
                    setTimeout(() => {
                        refetch();
                    }, 3000);
                })
                .catch((e: unknown) => {
                    message.destroy();
                    if (e instanceof Error) {
                        message.error({
                            content: t('Failed to update ownership type'),
                            duration: 3,
                        });
                    }
                });
        }
    };

    const onCancel = () => {
        // Clear the form state
        setOwnershipTypeBuilderState({
            name: '',
            description: '',
        });
        // Reset form fields
        form.resetFields();
        // Close the modal
        onClose();
    };

    const onUpsert = ownershipType ? onUpdateOwnershipType : onCreateOwnershipType;
    const titleText = ownershipType ? t('Edit Ownership Type') : t('Add Ownership Type');
    return (
        <Modal
            centered
            open={isOpen}
            title={titleText}
            onCancel={onCancel}
            className="f-modal"
            footer={
                <>
                    <Button onClick={onCancel} type="default" data-testid="ownership-builder-cancel">
                        {t('Cancel')}
                    </Button>
                    <Button
                        onClick={onUpsert}
                        type="primary"
                        disabled={!ownershipTypeBuilderState.name}
                        loading={isLoading}
                        data-testid="ownership-builder-save"
                    >
                        {t('Save')}
                    </Button>
                </>
            }
        >
            <Form form={form}>
                <FormItemContainer>
                    <FormItemTitle>{t('Name')}</FormItemTitle>
                    <StyledFormItem
                        name="name"
                        rules={[
                            {
                                required: true,
                                message: t('Please input a name for the ownership type'),
                            },
                            { whitespace: true },
                            { min: 1, max: 50 },
                        ]}
                    >
                        <Input
                            data-testid={NAME_INPUT_TEST_ID}
                            placeholder={t('Ownership type name')}
                            onChange={(e) => {
                                setName(e.target.value);
                            }}
                        />
                    </StyledFormItem>
                </FormItemContainer>
                <FormItemContainer className="pt-4">
                    <FormItemTitle>{t('Description')}</FormItemTitle>
                    <StyledFormItem name="description" rules={[{ whitespace: true }, { min: 1, max: 250 }]}>
                        <Input
                            data-testid={DESCRIPTION_INPUT_TEST_ID}
                            placeholder={t('Ownership type description')}
                            onChange={(e) => {
                                setDescription(e.target.value);
                            }}
                        />
                    </StyledFormItem>
                </FormItemContainer>
            </Form>
        </Modal>
    );
};
